import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Search,
  Mail,
  Phone,
  Calendar,
  MoreVertical,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  AlertTriangle,
  ArrowLeftRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { studentsAPI, transfersAPI } from '../lib/api';
import { daysUntil } from '../lib/dates';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import { StudentForm } from '../components/forms/StudentForm';
import type { Student, StudentFormData } from '../types';
import clsx from 'clsx';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function Students() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [deletingStudent, setDeletingStudent] = useState<Student | null>(null);
  const [pendingTransfer, setPendingTransfer] = useState<{ email: string; name: string } | null>(null);

  const { data: students, isLoading } = useQuery({
    queryKey: ['students', { search, active: showInactive ? undefined : true }],
    queryFn: () => studentsAPI.getAll({ search: search || undefined, active: showInactive ? undefined : true }),
  });

  const createMutation = useMutation({
    mutationFn: (data: StudentFormData) => studentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      setIsModalOpen(false);
      toast.success('Alumno creado exitosamente');
    },
    onError: (error: Error & { code?: string }, variables) => {
      // El alumno ya tiene otro entrenador → ofrecer transferencia en vez del error crudo.
      if (error.code === 'already_assigned') {
        setIsModalOpen(false);
        setPendingTransfer({
          email: variables.email,
          name: `${variables.firstName} ${variables.lastName}`.trim() || variables.email,
        });
        return;
      }
      toast.error(error.message);
    },
  });

  const transferMutation = useMutation({
    mutationFn: (email: string) => transfersAPI.request(email),
    onSuccess: () => {
      setPendingTransfer(null);
      toast.success('Solicitud enviada. El alumno tiene que aceptarla desde su app.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<StudentFormData> }) =>
      studentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      setEditingStudent(null);
      toast.success('Alumno actualizado exitosamente');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => studentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      setDeletingStudent(null);
      toast.success('Alumno eliminado exitosamente');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (data: StudentFormData) => {
    if (editingStudent) {
      updateMutation.mutate({ id: editingStudent.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="section-title flex items-center gap-3">
            <Users className="w-8 h-8 text-dark-100" />
            Alumnos
          </h1>
          <p className="section-subtitle">Gestiona tus alumnos y sus planes de entrenamiento</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5" />
          Nuevo Alumno
        </button>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants} className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-dark-300 text-sm">Mostrar inactivos</span>
          </label>
        </div>
      </motion.div>

      {/* Students List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {students?.map((student: Student) => (
              <motion.div
                key={student.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="card-hover group"
              >
                <div className="flex items-start justify-between mb-4">
                  <Link to={`/students/${student.id}`} className="flex items-center gap-3 flex-1">
                    <div className={clsx(
                      'w-12 h-12 rounded-full flex items-center justify-center text-dark-50 font-semibold text-lg',
                      student.isActive
                        ? 'bg-dark-700 ring-1 ring-primary-500/30'
                        : 'bg-dark-800'
                    )}>
                      {student.firstName[0]}{student.lastName[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-dark-100 group-hover:text-primary-400 transition-colors">
                        {student.firstName} {student.lastName}
                      </h3>
                      <div className="flex items-center gap-1 text-sm text-dark-400">
                        {student.relationshipStatus === 'former' ? (
                          <span className="inline-flex items-center gap-1 text-amber-400">
                            <UserX className="w-3 h-3" />
                            Ya no está con vos
                          </span>
                        ) : student.isActive ? (
                          <>
                            <UserCheck className="w-3 h-3 text-primary-500" />
                            Activo
                          </>
                        ) : (
                          <>
                            <UserX className="w-3 h-3 text-dark-500" />
                            Inactivo
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                  {/* Ex-alumno = read-only (la API bloquea editar/eliminar). Sin menú. */}
                  {student.relationshipStatus !== 'former' && (
                    <div className="relative group/menu">
                      <button className="p-1.5 rounded-lg text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      <div className="absolute right-0 top-full mt-1 w-36 py-1 bg-dark-800 border border-dark-600 rounded-lg shadow-xl opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all z-10">
                        <button
                          onClick={() => setEditingStudent(student)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-dark-300 hover:text-dark-100 hover:bg-dark-700 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          Editar
                        </button>
                        <button
                          onClick={() => setDeletingStudent(student)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-dark-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-dark-400">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{student.email}</span>
                  </div>
                  {student.phone && (
                    <div className="flex items-center gap-2 text-dark-400">
                      <Phone className="w-4 h-4" />
                      <span>{student.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-dark-400">
                    <Calendar className="w-4 h-4" />
                    <span>{student._count?.plans || 0} planes de entrenamiento</span>
                  </div>
                  {student.activePlan?.endDate && (() => {
                    const d = daysUntil(student.activePlan!.endDate!);
                    if (d > 7) return null;
                    return (
                      <div className={clsx(
                        'flex items-center gap-2 font-medium',
                        d < 0 ? 'text-red-400' : 'text-amber-400'
                      )}>
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span>
                          {d < 0
                            ? `Plan vencido hace ${Math.abs(d)} día${Math.abs(d) !== 1 ? 's' : ''}`
                            : d === 0
                            ? 'El plan vence hoy'
                            : `Plan vence en ${d} día${d !== 1 ? 's' : ''}`}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {student.notes && (
                  <p className="mt-3 pt-3 border-t border-dark-700/50 text-sm text-dark-400 line-clamp-2">
                    {student.notes}
                  </p>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Empty State */}
      {!isLoading && students?.length === 0 && (
        <motion.div variants={itemVariants} className="card text-center py-12">
          <Users className="w-16 h-16 mx-auto text-dark-600 mb-4" />
          <h3 className="text-lg font-semibold text-dark-300 mb-2">No hay alumnos</h3>
          <p className="text-dark-400 mb-6">
            {search ? 'No se encontraron alumnos con ese criterio de búsqueda' : 'Comienza agregando tu primer alumno'}
          </p>
          {!search && (
            <button onClick={() => setIsModalOpen(true)} className="btn-primary">
              <Plus className="w-5 h-5" />
              Agregar Alumno
            </button>
          )}
        </motion.div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen || !!editingStudent}
        onClose={() => {
          setIsModalOpen(false);
          setEditingStudent(null);
        }}
        title={editingStudent ? 'Editar Alumno' : 'Nuevo Alumno'}
      >
        <StudentForm
          initialData={editingStudent || undefined}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingStudent(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingStudent}
        onClose={() => setDeletingStudent(null)}
        title="Eliminar Alumno"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            ¿Estás seguro de que deseas eliminar a{' '}
            <span className="font-semibold text-dark-100">
              {deletingStudent?.firstName} {deletingStudent?.lastName}
            </span>
            ? Esta acción eliminará también todos sus planes de entrenamiento y no se puede deshacer.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeletingStudent(null)}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              onClick={() => deletingStudent && deleteMutation.mutate(deletingStudent.id)}
              disabled={deleteMutation.isPending}
              className="btn-danger"
            >
              {deleteMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Solicitar transferencia (el alumno ya tiene otro entrenador) */}
      <Modal
        isOpen={!!pendingTransfer}
        onClose={() => setPendingTransfer(null)}
        title="Solicitar transferencia"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            <span className="font-semibold text-dark-100">{pendingTransfer?.name}</span> ya tiene otro
            entrenador. Podés enviar una solicitud de transferencia: el alumno tiene que
            <span className="font-medium text-dark-200"> aceptarla desde su app</span> para pasar a ser tuyo.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setPendingTransfer(null)} className="btn-secondary">
              Cancelar
            </button>
            <button
              onClick={() => pendingTransfer && transferMutation.mutate(pendingTransfer.email)}
              disabled={transferMutation.isPending}
              className="btn-primary"
            >
              {transferMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <ArrowLeftRight className="w-4 h-4" />
                  Solicitar transferencia
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}

